import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import EmailField from 'components/Fields/EmailField/EmailField';
import TextField from 'components/Fields/TextField/TextField';
import SelectField from 'components/Fields/SelectField/SelectField';
import {
  withFullTranslationKey,
  CLAIM_DETAILS,
  OPTIONS_CLAIMANT_RELATIONSHIP_RIGHTSHOLDER_LIST,
  OPTIONS_RIGHTSHOLDER_ROLE_LIST,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import { getFieldValid, getFieldValue } from 'components/ContentReporting/form-utils';
import { FormData, FieldOnChangeEvent } from 'containers/ContentReporting/ContentLegalReporting/utils/types';

type RightsHolderFieldsProps = {
  formData: FormData;
  handleFieldChange: (fieldChangeEvent: FieldOnChangeEvent) => void;
  forceValidation: boolean;
  isAuthorizedAgentForRightsholder: boolean;
};

const RightsHolderFields: FunctionComponent<RightsHolderFieldsProps> = props => {
  const { formData, handleFieldChange, forceValidation, isAuthorizedAgentForRightsholder } = props;

  const { t } = useTranslation('infringement_claim_form');
  const fieldValue = getFieldValue(formData);
  const fieldValid = getFieldValid(formData);

  return (
    <>
      <SelectField
        field={{
          name: CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER,
          type: 'text',
          options: OPTIONS_CLAIMANT_RELATIONSHIP_RIGHTSHOLDER_LIST,
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        value={fieldValue(CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER)}
        valid={fieldValid(CLAIM_DETAILS.fields.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER)}
        tPrefix={withFullTranslationKey(CLAIM_DETAILS.translationPrefixes.CLAIMANT_RELATIONSHIP_RIGHTSHOLDER)}
        onFieldChange={handleFieldChange}
        forceValidation={forceValidation}
      />

      {isAuthorizedAgentForRightsholder && (
        <>
          <TextField
            field={{
              name: CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME,
              type: 'text',
              required: true,
              maxlength: 80,
              labelOverride: t(CLAIM_DETAILS.labels.RIGHTSHOLDER_NAME),
            }}
            value={fieldValue(CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME)}
            valid={fieldValid(CLAIM_DETAILS.fields.RIGHTSHOLDER_NAME)}
            onFieldChange={handleFieldChange}
            forceValidation={forceValidation}
            errorMessageKey="infringement_claim_form:field_required"
          />

          <EmailField
            field={{
              name: CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL,
              type: 'email',
              required: true,
              maxlength: 80,
              labelOverride: t(CLAIM_DETAILS.labels.RIGHTSHOLDER_EMAIL),
            }}
            value={fieldValue(CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL)}
            valid={fieldValid(CLAIM_DETAILS.fields.RIGHTSHOLDER_EMAIL)}
            onFieldChange={handleFieldChange}
            forceValidation={forceValidation}
            errorMessageKey="infringement_claim_form:field_required"
          />

          <SelectField
            field={{
              name: CLAIM_DETAILS.fields.RIGHTSHOLDER_ROLE,
              type: 'text',
              options: OPTIONS_RIGHTSHOLDER_ROLE_LIST,
              required: true,
              labelOverride: `_input${USE_LABEL_FROM_TRANSLATION_PREFIX}`,
            }}
            hasDefaultPlaceholder
            value={fieldValue(CLAIM_DETAILS.fields.RIGHTSHOLDER_ROLE)}
            valid={fieldValid(CLAIM_DETAILS.fields.RIGHTSHOLDER_ROLE)}
            tPrefix={withFullTranslationKey(CLAIM_DETAILS.translationPrefixes.RIGHTSHOLDER_ROLE)}
            onFieldChange={handleFieldChange}
            forceValidation={forceValidation}
          />
        </>
      )}
    </>
  );
};

export default RightsHolderFields;
